/**
 * Накладывает данные публикации из админки (news_publications) на статический пост блога.
 * Контент секций остаётся в статике; hero и SEO-картинка — только main_image из админки.
 */

function pickMainImageStrict(publication) {
  if (!publication) return null;
  return publication.mainImage || publication.main_image || null;
}

function pickPreviewImage(publication) {
  if (!publication) return null;
  return (
    publication.previewImage
    || publication.preview_image
    || publication.mainImage
    || publication.main_image
    || publication.image
    || null
  );
}

function stripHeroImages(post) {
  const merged = { ...post, image: null, previewImage: null };
  if (!Array.isArray(merged.structuredData)) return merged;

  merged.structuredData = merged.structuredData.map((block) => (
    block?.['@type'] === 'Article' ? { ...block, image: undefined } : block
  ));
  return merged;
}

function pickTakeaways(publication) {
  const items = publication?.keyTakeaways || publication?.key_takeaways;
  return Array.isArray(items) && items.length ? items : null;
}

function pickFaq(publication) {
  const items = publication?.faqItems || publication?.faq_items;
  if (!Array.isArray(items) || !items.length) return null;
  return items.filter((item) => item?.question && item?.answer);
}

function patchStructuredDataImage(structuredData, imagePath, siteBase) {
  if (!imagePath || !Array.isArray(structuredData)) return structuredData;
  const fullImage = imagePath.startsWith('http') ? imagePath : `${siteBase}${imagePath}`;
  return structuredData.map((block) => (
    block?.['@type'] === 'Article' ? { ...block, image: fullImage } : block
  ));
}

/**
 * @param {object|null|undefined} staticPost
 * @param {object|null|undefined} publication
 * @param {string} siteBase
 * @param {{ publicationResolved?: boolean }} options
 *   publicationResolved=false → hero не показываем (идёт загрузка API)
 *   publicationResolved=true, publication=null → legacy static fallback
 *   publicationResolved=true, publication set → только main_image из админки
 */
export function mergeBlogPostWithPublication(
  staticPost,
  publication,
  siteBase = 'https://antologia.tech',
  { publicationResolved = false } = {},
) {
  if (!staticPost) return null;

  if (!publicationResolved) {
    return stripHeroImages(staticPost);
  }

  if (!publication) {
    return staticPost;
  }

  const merged = stripHeroImages(staticPost);
  const mainImage = pickMainImageStrict(publication);
  const previewImage = pickPreviewImage(publication);

  if (mainImage) merged.image = mainImage;
  if (previewImage) merged.previewImage = previewImage;
  if (publication.title) merged.title = publication.title;
  if (publication.description) merged.description = publication.description;

  const seoTitle = publication.metaTitle || publication.meta_title;
  if (seoTitle) merged.seoTitle = seoTitle;

  const keywords = publication.metaKeywords || publication.meta_keywords;
  if (keywords) merged.keywords = keywords;

  const displayDate = publication.displayDate || publication.display_date || publication.date;
  if (displayDate) merged.date = displayDate;

  const publishedAt = publication.publishedAt || publication.published_at;
  if (publishedAt) {
    merged.datePublishedISO = String(publishedAt).slice(0, 10);
    merged.dateModifiedISO = merged.dateModifiedISO || merged.datePublishedISO;
  }

  if (publication.author) merged.author = publication.author;

  const takeaways = pickTakeaways(publication);
  if (takeaways) merged.keyTakeaways = takeaways;

  const faq = pickFaq(publication);
  if (faq) merged.faq = faq;

  if (mainImage && merged.structuredData) {
    merged.structuredData = patchStructuredDataImage(
      merged.structuredData,
      mainImage,
      siteBase.replace(/\/$/, ''),
    );
  }

  return merged;
}

export function mergeBlogListItemWithPublication(listItem, publication) {
  if (!listItem) return null;
  if (!publication) return listItem;

  const image = pickPreviewImage(publication);
  return {
    ...listItem,
    ...(publication.title ? { title: publication.title } : {}),
    ...(publication.description ? { description: publication.description } : {}),
    ...(image ? { image } : {}),
    ...(publication.displayDate || publication.display_date || publication.date
      ? { date: publication.displayDate || publication.display_date || publication.date }
      : {}),
  };
}

export function mergeBlogListWithPublications(staticList, publications = []) {
  const bySlug = new Map(
    publications.filter((item) => item?.slug).map((item) => [item.slug, item]),
  );

  return staticList.map((item) => mergeBlogListItemWithPublication(item, bySlug.get(item.slug)));
}
